import React from 'react';
import _ from 'lodash';
import {CURRENCY, CURRENCY_CODE, CURRENCY_MENU_OPTIONS, DROPZONE_ACCEPTED_FILES, DROPZONE_ACCEPTED_FILES_ARRAY} from "../../helpers/variables";
import { getDateQuestionMaximumValue, getDateQuestionMinimumValue } from "../../helpers/questions/functions";
import { FormGroup } from 'arag-ui-lib';
import moment from 'moment';
import { filterActiveOptions, amountOnChange } from "../../helpers/functions";
import { FORM_GROUP_VARIANTS, INPUT_TYPE } from '../../helpers/formGroupVariants';
import { DATE_FORMAT, UPDATED_LNC_MN } from '../../helpers/constants';
import CONTENT_STRINGS from '../../helpers/contentStrings';
import { sanitizeInfoText } from '../../helpers/commentParser';
import useAmountField from '../../hooks/useAmountField';

const PolicyTabUpdateQuestion = (props) => {

    const {
        question,
        formData,
        HandleFormCompChange,
        HandleSelectChange,
        HandleDateChange,
        OnDrop,
        onDropMultiple,
        onDropSingle,
        allQuestions,
        shouldUpdateQuestionBeMandatory,
        pigApplied,
    } = props;
    const { error: amountError, validate: amountValidate, onBlur: amountOnBlur, decimalError, checkDecimal } = useAmountField({
        name: `${question?.id}`,
        value: formData[question?.id],
        minimum: question?.minimumValue,
        onChange: HandleFormCompChange,
        enabled: question?.inputType === INPUT_TYPE.AMOUNT,
    });
    // WI 95280: the updated L&C spend is frozen once a PIG disbursement request has been sent. Its parent
    // question stays answerable per AC1, and `required` is deliberately left as seeded (IsRequired = true) so
    // no value can be silently recorded as empty - answering "Yes" therefore dead-ends until the BA confirms
    // whether the parent should be hidden instead.
    // `pigApplied` arrives as a prop rather than being read from the store here: this component is shared with
    // the External Policy update modal, which is driven by the externalPolicyUpdate slice, and
    // newApplication.selectedApplication is not cleared when that modal opens - reading it directly would
    // disable the field based on whichever gATEway policy happened to be viewed last.
    const isPigLockedAmount = question?.machineName === UPDATED_LNC_MN && !!pigApplied;

    const isMandatory = shouldUpdateQuestionBeMandatory(question);
    if (!isMandatory) {
        return null;
    }

    //calendar question?
    if(question.inputType === INPUT_TYPE.CALENDAR){
        let value =  formData[question?.id];
        // date field
        let maxDate , minDate ;
        minDate = getDateQuestionMinimumValue(question, formData, allQuestions);
        maxDate = getDateQuestionMaximumValue(question, formData, allQuestions);
        minDate = moment(minDate,"DD/MM/YYYY").format("YYYY/MM/DD");
        maxDate = moment(maxDate,"DD/MM/YYYY").format("YYYY/MM/DD");
        //When new application components get update please rework logic without "moment" library. 
        return (
            <div className='parent inline-flex-parent'>
                <FormGroup
                    labelSize={"col-sm-4"}
                    inputSize={"col-sm-6"}
                    label={question.name}
                    variant={FORM_GROUP_VARIANTS.DATE}
                    infoText={sanitizeInfoText(question?.tooltip)}
                    inputProps={{
                        name: `${question?.id}`,
                        required: question?.isRequired,
                        label: question?.name, 
                        dateInputProps: {
                            name:question.id,
                            onChange: (e) => HandleDateChange(question?.id,new Date(e),minDate,maxDate),
                            minDate: new Date(minDate),
                            maxDate: new Date(maxDate),
                            autoComplete: 'off',
                            dateFormat: DATE_FORMAT,
                            selected: value,
                            required: question.isRequired,
                            placeholderText: CONTENT_STRINGS.DATE_PLACEHOLDER,
                        }
                    }}
                    formGroupTestId={`policy-update-${question.machineName}`}
                    tooltipTestId={`policy-update-${question.machineName}-info`}
                />
            </div>
        )
    }

    //amount question?
    if(question.inputType === INPUT_TYPE.AMOUNT){
        return (
            <div className='currencyField'>
                <FormGroup
                    errorMessage={decimalError || amountError}
                    labelSize={"col-sm-4"}
                    inputSize={"col-sm-6"}
                    label={question?.name}
                    variant={FORM_GROUP_VARIANTS.CURRENCY}
                    inputProps={{
                        name: `${question?.id}`,
                        value: formData[question?.id] || '',
                        onBlur: amountOnBlur,
                        onChange: (e) => { if (!checkDecimal(e.target.value)) amountOnChange(HandleFormCompChange, amountValidate)(e); },
                        disabled: isPigLockedAmount,
                        disabledDropdown: true,
                        handleOptionSelect: () => {},
                        menuOptions: CURRENCY_MENU_OPTIONS,
                        selectedCurrency: { sign: `${CURRENCY}`, code: `${CURRENCY_CODE}` },
                        required: question?.isRequired
                    }}
                    // WI 95280: FormGroup renders infoText inside its own row, straight after the input
                    // column, which puts the lock explanation on the same line as the amount field.
                    infoText={isPigLockedAmount ? CONTENT_STRINGS.PIG_APPLIED_EDIT_LOCKED_TOOLTIP : undefined}
                    formGroupTestId={`policy-update-${question.machineName}`}
                    tooltipTestId={`policy-update-${question.machineName}-info`}
                />
            </div>
        )
    }

    //text question?
    if(question.inputType === INPUT_TYPE.TEXT){
        return (
            <FormGroup
                labelSize={"col-sm-4"}
                inputSize={"col-sm-6"}
                label={question?.name}
                variant={FORM_GROUP_VARIANTS.TEXT}
                inputProps={{
                    onChange: (evt) => HandleFormCompChange(evt.target.name,evt.target.value),
                    name: `${question?.id}`,
                    value: formData[question?.id] || '',
                    required: question?.isRequired
                }}
                formGroupTestId={`policy-update-${question.machineName}`}
            />
        )
    }

    //text area question?
    if(question.inputType === INPUT_TYPE.TEXT_AREA){
        return (
            <FormGroup
                labelSize={"col-sm-4"}
                inputSize={"col-sm-6"}
                label={question.name}
                variant={FORM_GROUP_VARIANTS.TEXT_AREA}
                inputProps={{
                    onChange:(evt) => HandleFormCompChange(question.id,evt.target.value),
                    name: `${question?.id}`,
                    value: formData[question?.id] || '',
                    required: question?.isRequired,
                    rows: 6
                }}
                formGroupTestId={`policy-update-${question.machineName}`}
            />
        )
    }

    //drop down question?
    if(question.inputType === INPUT_TYPE.DROP_DOWN){
        let selectedOption = _.find(question.options, o => {return o.id && formData[question.id] && o.id.toString() === formData[question.id].toString()});
        let childQuestions  = [];
        if(selectedOption){
            childQuestions  = _.filter(allQuestions, q => {return _.find(selectedOption.childQuestions, cq => {return cq.id === q.id})});
        }
        const dropdownOptions = [
            ...[{key: '', label: CONTENT_STRINGS.NONE}],
            ...filterActiveOptions(question.options, formData[question.id]).map(o => ({key:o.id,value: o.id, label: o.name}))
        ]
        return (
            <div>
                <FormGroup
                    labelSize={"col-sm-4"}
                    inputSize={"col-sm-6"}
                    label={question.name}
                    variant={FORM_GROUP_VARIANTS.SELECT}
                    inputProps={{
                        onChange:HandleSelectChange,
                        options: dropdownOptions,
                        value: formData[question.id],
                        name: `${question.id}`,
                        required:question.isRequired
                    }}
                    infoText={sanitizeInfoText(question?.tooltip)}
                    tooltipTestId={`policy-update-${question.machineName}-info`}
                    formGroupTestId={`policy-update-${question.machineName}`}
                />
                {
                    _.sortBy(childQuestions,['sortOrder']).map(cq => {
                        return (
                            <PolicyTabUpdateQuestion
                                shouldUpdateQuestionBeMandatory={shouldUpdateQuestionBeMandatory}
                                allQuestions={allQuestions}
                                key={cq.id}
                                HandleSelectChange={HandleSelectChange}
                                HandleFormCompChange={HandleFormCompChange}
                                HandleDateChange={HandleDateChange}
                                question={cq}
                                formData={formData}
                                OnDrop={OnDrop}
                                onDropMultiple={onDropMultiple}
                                onDropSingle={onDropSingle}
                                pigApplied={pigApplied}
                            />
                        )
                    })
                }
            </div>
        )
    }

    if (question.inputType === INPUT_TYPE.UPLOAD) {
        const fileValue= formData[question.id] ? formData[question.id].map((f,index) => ({...f,value:index,label:f.name})):[];

        return <div>
            <FormGroup
                labelSize={"col-sm-4"}
                inputSize={"col-sm-6"}
                label={question.name}
                variant={FORM_GROUP_VARIANTS.FILE}
                inputProps={{
                    name: question.id,
                    label: fileValue[0]? fileValue[0].name : CONTENT_STRINGS.ADD_FILE,
                    accepts: DROPZONE_ACCEPTED_FILES,
                    allowMultiple: false,
                    required: false, //the file is required at least once, the API will validate it
                    onDrop: (evt) => onDropSingle(evt,question.id)
                }}
                formGroupTestId={`policy-update-${question.machineName}`}
            />
        </div>
    }
    if (question.inputType === INPUT_TYPE.MULTI_UPLOAD) {
        const filesValues = formData[question.id] ? formData[question.id].map((f,index) => ({...f,value:index,label:f.name})) : [];
        const uploadedFilesText = filesValues.map(file => file.name).join(", ");

        return (
            <div className='form-group'>
                <div className='multiSelectField'>
                    <FormGroup
                        labelSize={"col-md-4"}
                        inputSize={"col-md-6"}
                        label={question.name}
                        variant={FORM_GROUP_VARIANTS.FILE}
                        inline
                        helpText={uploadedFilesText}
                        inputProps={{
                            name: question.id,
                            label: filesValues.length ? CONTENT_STRINGS.ADD_MORE_FILES : CONTENT_STRINGS.ADD_FILE,
                            accepts: DROPZONE_ACCEPTED_FILES_ARRAY,
                            allowMultiple: true,
                            required: false,
                            onDrop: (evt) => onDropMultiple(evt,question.id),
                        }}
                        formGroupTestId={`policy-update-${question.machineName}`}
                    />
                </div>
            </div>
        )
    }

    if (question.inputType === INPUT_TYPE.NO_INPUT) {
        return <div>
            <FormGroup
                labelSize={'col-sm-12'}
                inputSize={'col-sm-12'}
                label={""}
                variant={FORM_GROUP_VARIANTS.PLAIN}
                inputProps={{
                    name: question.id,
                    label: question.name,
                }}
                formGroupTestId={`policy-update-${question.machineName}`}
            />
        </div>
    }
    return null
};

export default PolicyTabUpdateQuestion;